import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';


import { ServerParseError } from '@apollo/client';
import { User } from '@auth0/auth0-react';
import useAsyncEffect from 'use-async-effect';

import { useAuthCtx } from '@anyx/common-auth';
import { MdUserRole, SelfDetailsQuery } from '@anyx/common-graphql';
import { FirebaseHelpers } from '@anyx/external-firebase';
import {
  CurrencyUtils,
  LanguageUtils,
  TimezoneUtils,
} from '@anyx/shared-utils';

import { useSelfDetails } from './operation';
import { Self, SelfContext, defaultSelf } from './SelfContext';

export const SelfProvider = ({
  children,
  unavailableLink,
  permissionPath,
}: {
  children: React.ReactNode | React.ReactNode[];
  unavailableLink: string;
  permissionPath: string;
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { authenticated, user, logout } = useAuthCtx();

  const { selfDetails, error, refetch } = useSelfDetails(authenticated);

  const [isSet, setIsSet] = useState<boolean>(false);

  // When not authenticated, we're "done" - nothing to load
  // This lets LoadingGate pass through so Auth0Guard in routes can redirect to login
  useEffect(() => {
    if (!authenticated) {
      setIsSet(true);
    }
  }, [authenticated]);

  const getSelf = useCallback(
    (anyXUser: SelfDetailsQuery['MdSelf'] | undefined, auth0User: User | undefined): Self => ({
      ...defaultSelf.self,
      ...anyXUser,
      lastName: anyXUser?.lastName ?? '',
      email: auth0User?.email || '',
      picture: auth0User?.picture,
      name: LanguageUtils.localizeName(
        anyXUser?.firstName,
        anyXUser?.lastName ?? undefined,
        LanguageUtils.fromI18nLanguage(i18n.language)
      ),
      language: LanguageUtils.fromI18nLanguage(anyXUser?.language),
    }),
    [i18n.language]
  );

  const self = useMemo(() => getSelf(selfDetails, user), [selfDetails, getSelf, user]);

  // User Settings - only run when we have actual self data (not defaults)
  useAsyncEffect(async () => {
    // Guard: Only process when selfDetails has loaded (not default values)
    if (!selfDetails) return;

    const { role, id, accounts } = self;

    LanguageUtils.setUserLocale(self?.language);
    LanguageUtils.setLanguage(self?.language);

    TimezoneUtils.setTimezone(self?.timezoneId);
    CurrencyUtils.setCurrency(self?.currency);

    FirebaseHelpers.setUser(id, {
      role,
      account:
        role === MdUserRole.MERCHANT
          ? {
              accountId: accounts?.[0]?.id || '',
              name: accounts?.[0]?.name || '',
            }
          : undefined,
      isInternalTester: !!user?.[permissionPath]?.includes('category:internal_tester'),
      isInternalUser: !!user?.[permissionPath]?.includes('category:internal_user'),
    });

    setIsSet(true);

     
  }, [self, selfDetails]);

  // Logout user in case of 401 or UNAUTHORIZED response to `getSelf` This should technically happen
  // Only when the user reload the page and the token / refresh token is expired
  useEffect(() => {
    if (error) {
      // Apollo Client v4: ErrorLike no longer has networkError property
      // Check if error has response property directly (for ServerParseError)
      if ('response' in error) {
        const serverError = error as unknown as ServerParseError;

        if (serverError.statusCode === 401) {
          logout();
          return;
        }
      }
      if (error?.message === 'UNAUTHORIZED') {
        logout();
        return;
      }

      if (window.location.pathname !== unavailableLink) {
        navigate(unavailableLink);
      }
    }
  }, [error, logout, navigate, unavailableLink]);

  return (
    <SelfContext.Provider
      value={{
        self,
        refetch,
        isSet,
      }}
    >
      {children}
    </SelfContext.Provider>
  );
};
